/**
 * Country loading utility
 * Loads country lists dynamically based on language with fallback to English
 */

export interface Country {
  code: string;
  name: string;
}

// Cache for loaded country data
const countryCache: Map<string, Country[]> = new Map();

/**
 * Loads country list for a given language
 * Falls back to English if the language file doesn't exist
 * @param language - Language code (e.g., 'en', 'pl')
 * @returns Promise resolving to array of countries
 */
export async function loadCountries(language: string): Promise<Country[]> {
  // Normalize language code (e.g., 'en-US' -> 'en')
  const normalizedLang = language.split('-')[0].toLowerCase();

  // Check cache first
  if (countryCache.has(normalizedLang)) {
    return countryCache.get(normalizedLang)!;
  }

  try {
    // Get base URL and check for CDN (same pattern as i18n config)
    const spacesUrl = import.meta.env.VITE_SPACES_URL?.replace(/\/$/, '');
    const baseUrl = import.meta.env.BASE_URL || '/';
    
    let countriesPath: string;
    if (spacesUrl) {
      // Production/Dev Deploy: Load from CDN
      const normalizedBaseUrl = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`;
      countriesPath = `${spacesUrl}${normalizedBaseUrl}locales/${normalizedLang}/countries.json`;
      console.log(`[countries] Loading countries from CDN: ${countriesPath}`);
    } else {
      // Local Development: Load from local public folder
      countriesPath = `${baseUrl}locales/${normalizedLang}/countries.json`;
      console.log(`[countries] Loading countries locally: ${countriesPath}`);
    }
    
    // Try to load the requested language
    const response = await fetch(countriesPath);
    
    if (response.ok) {
      const countries = await response.json();
      countryCache.set(normalizedLang, countries);
      return countries;
    } else {
      // If requested language fails and it's not English, try English fallback
      if (normalizedLang !== 'en') {
        console.warn(`[countries] Failed to load countries for language '${normalizedLang}', falling back to English`);
        return loadCountries('en');
      } else {
        throw new Error(`Failed to load countries: ${response.statusText}`);
      }
    }
  } catch (error) {
    // If error and not English, try English fallback
    if (normalizedLang !== 'en') {
      console.warn(`[countries] Error loading countries for language '${normalizedLang}', falling back to English:`, error);
      return loadCountries('en');
    } else {
      console.error('[countries] Failed to load English countries:', error);
      // Return empty array as last resort
      return [];
    }
  }
}

